
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface EnrichmentDebugPanelProps {
  lastPrompt: string;
  lastResponse: string;
  responseTime: number;
  timestamp: string;
  model: string;
  provider?: string;
  error?: string;
}

const EnrichmentDebugPanel = ({
  lastPrompt,
  lastResponse,
  responseTime,
  timestamp,
  model,
  provider,
  error
}: EnrichmentDebugPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard`);
  };

  if (!isOpen && (!lastPrompt || !lastResponse)) {
    return (
      <div className="flex justify-center mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsOpen(true)}
          disabled={!lastPrompt && !lastResponse}
        >
          <Eye className="h-4 w-4 mr-1" /> Show Enrichment Debug Panel
        </Button>
      </div>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Determine which API provider was used
  const getProviderName = (modelName: string, providerInfo?: string) => {
    if (providerInfo) {
      if (providerInfo.includes("perplexity")) {
        return "Perplexity AI";
      } else if (providerInfo.includes("openai")) {
        return "OpenAI";
      }
    }
    
    if (!modelName) return "AI Provider";
    
    if (modelName.includes("llama") || modelName.includes("sonar") || modelName.startsWith("pplx")) {
      return "Perplexity AI";
    } else if (modelName.includes("gpt")) {
      return "OpenAI";
    } else {
      return "AI Provider";
    }
  };

  const providerName = getProviderName(model, provider);
  const providerColor = providerName === "Perplexity AI" ? "text-purple-600" : "text-green-600";

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center">
          Investor Enrichment Debug
          <span className={`ml-2 text-sm font-normal ${providerColor}`}>
            ({providerName})
          </span>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {isOpen ? " Hide Details" : " Show Details"}
        </Button>
      </CardHeader>
      
      {isOpen && (
        <CardContent>
          <div className="text-sm text-gray-500 mb-3 space-y-1">
            {responseTime > 0 && (
              <p>Response time: {(responseTime / 1000).toFixed(2)} seconds</p>
            )}
            {timestamp && (
              <p>Timestamp: {formatTimestamp(timestamp)}</p>
            )}
            {model && (
              <p>Model: {model}</p>
            )}
            {provider && (
              <p>Provider: {provider}</p>
            )}
            {error && (
              <p className="text-red-500">Error: {error}</p>
            )}
          </div>
          
          <Tabs defaultValue="prompt">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="prompt">Prompt Sent to {providerName}</TabsTrigger>
              <TabsTrigger value="response">Response from {providerName}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="prompt" className="mt-4">
              <div className="flex justify-end mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(lastPrompt, "Prompt")}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <Textarea 
                value={lastPrompt} 
                readOnly 
                className="h-[300px] font-mono text-xs"
              />
            </TabsContent>
            
            <TabsContent value="response" className="mt-4">
              <div className="flex justify-end mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(lastResponse, "Response")}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <Textarea 
                value={lastResponse} 
                readOnly 
                className="h-[300px] font-mono text-xs"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      )}
    </Card>
  );
};

export default EnrichmentDebugPanel;
